import { create } from 'zustand';

import {
  listSurgeries,
  createSurgery as apiCreateSurgery,
  updateSurgery as apiUpdateSurgery,
  deactivateSurgery as apiDeactivateSurgery,
  setSurgeryDepartments as apiSetSurgeryDepartments,
} from '../services/api/surgeryApi';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * Surgery catalog store (B2 / S4–S5) — Zustand
 *
 * Clone of bedCategoryStore.js keyed by `surgery_id` (org-global, no dept
 * slices). Idempotent + single-flight fetch — bounded requests (memory rule:
 * runtime safety / no request storms). Server machine-readable error codes
 * (PACKAGE_PRICE_REQUIRED / ITEMIZED_NEEDS_COMPONENT) pass through verbatim
 * on the result for the page's field-level mapping.
 * ═══════════════════════════════════════════════════════════════════════
 */

const useSurgeryStore = create((set, get) => ({
  // ── State ──────────────────────────────────────────────────────────
  surgeries: [],
  isLoading: false,
  error: null,
  includeInactive: false,
  loaded: false,
  _inFlight: null,

  // ── Fetch (bounded: single-flight + loaded guard) ──────────────────
  fetchSurgeries: async ({ force = false } = {}) => {
    const { _inFlight, loaded } = get();
    if (_inFlight) return _inFlight;
    if (loaded && !force) {
      return { success: true, data: { surgeries: get().surgeries } };
    }
    set({ isLoading: true, error: null });
    const promise = (async () => {
      const result = await listSurgeries({ include_inactive: get().includeInactive });
      if (result.success) {
        set({
          surgeries: result.data.surgeries || [],
          isLoading: false,
          loaded: true,
          _inFlight: null,
        });
      } else {
        set({ error: result.error, isLoading: false, _inFlight: null });
      }
      return result;
    })();
    set({ _inFlight: promise });
    return promise;
  },

  // ── Create → refetch ───────────────────────────────────────────────
  createSurgery: async (body) => {
    const result = await apiCreateSurgery(body);
    if (result.success) {
      await get().fetchSurgeries({ force: true });
    }
    return result;
  },

  // ── Update (partial) — patch in place by id ────────────────────────
  updateSurgery: async (surgery_id, body) => {
    const result = await apiUpdateSurgery(surgery_id, body);
    if (result.success) {
      set((s) => ({
        surgeries: s.surgeries.map((r) =>
          r.surgery_id === surgery_id ? { ...r, ...(result.data || body) } : r
        ),
      }));
    }
    return result;
  },

  // ── B5 / S1 — dept tagging ("Visible to"). Patches the row in place with
  // the server's post-flip owner_scope + department_ids (single source). ──
  setSurgeryDepartments: async (surgery_id, department_ids) => {
    const result = await apiSetSurgeryDepartments(surgery_id, department_ids);
    if (result.success && result.data) {
      set((s) => ({
        surgeries: s.surgeries.map((r) =>
          r.surgery_id === surgery_id
            ? {
                ...r,
                owner_scope: result.data.owner_scope,
                department_ids: result.data.department_ids || [],
              }
            : r
        ),
      }));
    }
    return result;
  },

  // ── Deactivate (soft-delete) ───────────────────────────────────────
  deactivateSurgery: async (surgery_id) => {
    const result = await apiDeactivateSurgery(surgery_id);
    if (result.success) {
      const { includeInactive } = get();
      set((s) => ({
        surgeries: includeInactive
          ? s.surgeries.map((r) => (r.surgery_id === surgery_id ? { ...r, active: false } : r))
          : s.surgeries.filter((r) => r.surgery_id !== surgery_id),
      }));
    }
    return result;
  },

  // ── Backend filter: refetch on flip ────────────────────────────────
  setIncludeInactive: (value) => {
    set({ includeInactive: !!value });
    return get().fetchSurgeries({ force: true });
  },

  clearError: () => set({ error: null }),
}));

export default useSurgeryStore;
